import React, { useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  Clock3,
  Loader2,
  Minus,
  Plus,
  PlusCircle,
  TrendingUp,
  Users,
  XCircle,
  Zap,
} from "lucide-react";
import {
  buildPickLabel,
  challengeIsOpen,
  counterPayout,
  formatCash,
  isChatChallengeJoinedByMe,
  resolveCreatorBudget,
  resolveLeagueName,
  resolveMinStake,
  resolveOfferedOdds,
  resolveRemainingCapacity,
} from "../../utils/bondchat/chatChallengeHelpers";

const Team = ({ team }) => (
  <div className="bc-challenge-team">
    {team?.logo ? (
      <img src={team.logo} alt="" className="bc-challenge-team-logo" />
    ) : (
      <span className="bc-challenge-team-logo-fallback" aria-hidden>
        ⚽
      </span>
    )}
    <strong title={team?.name}>{team?.name || "Team"}</strong>
  </div>
);

const ChatChallengeCard = ({ msg, userId, onCounter }) => {
  const challenge = msg?.chatChallenge;
  const [busy, setBusy] = useState(false);
  const [stakeInput, setStakeInput] = useState("");

  const minStake = resolveMinStake(challenge);
  const maxStake = resolveRemainingCapacity(challenge);
  const offeredOdds = resolveOfferedOdds(challenge);
  const creatorBudget = resolveCreatorBudget(challenge);
  const capacityOpen = maxStake >= minStake;
  const msgId = msg?._id;

  useEffect(() => {
    setStakeInput(String(minStake));
  }, [msgId, minStake, maxStake]);

  const stake = useMemo(() => {
    const n = parseFloat(String(stakeInput).replace(/[^0-9.]/g, ""));
    if (!Number.isFinite(n) || n <= 0) return 0;
    const floored = Math.max(minStake, n);
    return maxStake > 0 ? Math.min(floored, maxStake) : floored;
  }, [stakeInput, minStake, maxStake]);

  if (!challenge?.fixtureRef) return null;

  const fixture = challenge.fixtureRef;
  const leagueName = resolveLeagueName(fixture);
  const open = challengeIsOpen(challenge);
  const joined = Math.max(0, Number(challenge.joinedPeople || 0));
  const outcomeLabel = challenge.agreedOdds
    ? buildPickLabel(fixture, challenge.agreedOdds)
    : challenge.pickLabel || "Selected outcome";
  const isAgainst = challenge.stance === "against";
  const stanceCaption = isAgainst ? "Against" : "For";
  const stanceHint = isAgainst
    ? "Creator fades this — wins if it does not happen"
    : "Creator backs this — wins if it happens";
  const isCreator = String(msg?.userId?._id || msg?.userId) === String(userId);
  const alreadyAccepted = isChatChallengeJoinedByMe(msg, userId);
  const canAccept = open && !isCreator && !alreadyAccepted && capacityOpen && msg?._id;
  const canAddStake =
    open && !isCreator && alreadyAccepted && capacityOpen && msg?._id;
  const potentialWin = counterPayout(stake, offeredOdds);
  const message =
    msg.message?.trim() && !msg.message.startsWith("⚔️ Challenge:")
      ? msg.message.trim()
      : "";

  const handleAccept = async () => {
    if (busy || !(canAccept || canAddStake) || stake < minStake) return;
    setBusy(true);
    try {
      await onCounter?.(msg._id, stake);
    } catch {
      // Toast already shown from socket handlers.
    } finally {
      setBusy(false);
    }
  };

  const adjustStake = (delta) => {
    setStakeInput((prev) => {
      const base = parseFloat(String(prev).replace(/[^0-9.]/g, "")) || minStake;
      const next = Math.max(minStake, base + delta);
      const capped = maxStake > 0 ? Math.min(next, maxStake) : next;
      return String(Math.round(capped));
    });
  };

  const statusLabel = isCreator
    ? open
      ? "Waiting for counters"
      : "Challenge closed"
    : alreadyAccepted
      ? open
        ? "Challenge accepted"
        : "Challenge closed"
      : "Challenge closed";

  const actionLabel = (() => {
    if (busy) return canAddStake ? "Adding…" : "Accepting…";
    if (canAddStake) return `Add Cash ${stake.toLocaleString()}`;
    return `Counter · win ${potentialWin.toLocaleString()}`;
  })();

  return (
    <div className="bc-challenge-card">
      <span className="bc-challenge-card-accent" aria-hidden />

      <header className="bc-challenge-card-head">
        <div className="bc-challenge-card-title-wrap">
          <span className="bc-challenge-card-flash" aria-hidden>
            <Zap size={12} />
          </span>
          <span className="bc-challenge-card-title">
            <strong>Open challenge</strong>
            {leagueName ? <small>{leagueName}</small> : null}
          </span>
        </div>

        <div className="bc-challenge-card-pills">
          <span className="bc-challenge-card-pill">
            <strong>{formatCash(creatorBudget)}</strong>
            <small>budget</small>
          </span>
          <span className="bc-challenge-card-pill">
            <strong>{formatCash(minStake)}</strong>
            <small>min</small>
          </span>
          <span className="bc-challenge-card-pill">
            <TrendingUp size={11} />
            <strong>{offeredOdds.toFixed(2)}</strong>
            <small>odds</small>
          </span>
          <span className="bc-challenge-card-pill">
            <Users size={11} />
            <strong>{joined}</strong>
            <small>in</small>
          </span>
        </div>
      </header>

      <div className="bc-challenge-card-body">
        <div
          className={`bc-challenge-card-pick ${
            isAgainst ? "is-against" : "is-for"
          }`}
        >
          <div className="bc-challenge-card-pick-head">
            <span className="bc-challenge-stance-badge">
              {isAgainst ? <XCircle size={11} /> : <CheckCircle2 size={11} />}
              {stanceCaption}
            </span>
            <small>{stanceHint}</small>
          </div>
          <strong>{outcomeLabel}</strong>
        </div>

        <div className="bc-challenge-match-board">
          <Team team={fixture.homeTeam} />
          <span className="bc-challenge-versus">VS</span>
          <Team team={fixture.awayTeam} />
        </div>

        {message ? (
          <p className="bc-challenge-card-message">"{message}"</p>
        ) : null}

        {canAccept || canAddStake ? (
          <div className="bc-challenge-actions">
            <div className="bc-challenge-spots-stepper" aria-label="Counter stake">
              <button
                type="button"
                className="bc-challenge-spot-btn"
                onClick={() => adjustStake(-500)}
                disabled={busy || stake <= minStake}
                aria-label="Decrease stake"
              >
                <Minus size={14} />
              </button>
              <span className="bc-challenge-spot-value">
                <input
                  type="number"
                  inputMode="numeric"
                  min={minStake}
                  max={maxStake > 0 ? maxStake : undefined}
                  value={stakeInput}
                  onChange={(e) => setStakeInput(e.target.value.replace(/[^0-9.]/g, ""))}
                  className="bc-challenge-stake-input"
                  aria-label="Stake amount"
                  disabled={busy}
                />
                <small>
                  min {formatCash(minStake)} · max {formatCash(maxStake)}
                </small>
              </span>
              <button
                type="button"
                className="bc-challenge-spot-btn"
                onClick={() => adjustStake(500)}
                disabled={busy || (maxStake > 0 && stake >= maxStake)}
                aria-label="Increase stake"
              >
                <Plus size={14} />
              </button>
            </div>
            <button
              type="button"
              className={`bc-challenge-counter-btn${
                canAddStake ? " is-add-spots" : ""
              }`}
              disabled={busy || stake < minStake}
              onClick={handleAccept}
            >
              {busy ? (
                <Loader2 size={14} className="bc-spin" />
              ) : canAddStake ? (
                <PlusCircle size={14} />
              ) : (
                <Zap size={14} />
              )}
              <strong>{actionLabel}</strong>
            </button>
          </div>
        ) : (
          <div
            className={`bc-challenge-status${
              alreadyAccepted ? " is-accepted" : ""
            }`}
          >
            {alreadyAccepted ? (
              <CheckCircle2 size={13} />
            ) : (
              <Clock3 size={13} />
            )}
            <span>{statusLabel}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatChallengeCard;
